import { type RefObject, useEffect, useRef } from 'react'

/**
 * What counts as focusable for the trap.
 *
 * `:not([disabled])` matters on the capture form specifically: Save is disabled at zero characters,
 * and without the exclusion Tab would land on a control that cannot be activated.
 */
const FOCUSABLE =
  'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'

/**
 * The four behaviours a hand-rolled modal owes its user: Escape closes, focus is trapped inside,
 * focus returns where it came from, and the page behind does not scroll.
 *
 * **This lives in one place because it was written twice.** `ui/sheet.tsx` and
 * `components/PhotoViewer.tsx` held character-for-character identical copies of this effect *and* of
 * the selector above, with a comment on one asking the next reader to "keep them in step" by hand —
 * which is a bug waiting for whoever fixes a trap bug in only one of them. `Sheet`'s own note
 * explains why the behaviours are hand-rolled at all rather than pulled from `@radix-ui/react-dialog`
 * or native `<dialog>`; that reasoning is unchanged, this is only the shared body of it.
 *
 * @param panelRef  The dialog element. Everything focusable inside it is in the trap; the element
 *                  itself is the fallback focus target, so give it `tabIndex={-1}`.
 * @param onClose   Called on Escape. Must be stable, or the listener is torn down and rebuilt on
 *                  every render.
 * @param active    `false` while the modal is closed but still mounted — `Sheet` renders `null`
 *                  rather than unmounting its caller, so it needs the effect to stand down without
 *                  the hook being called conditionally.
 */
export function useFocusTrap(
  panelRef: RefObject<HTMLElement | null>,
  onClose: () => void,
  active = true,
): void {
  /** Where focus was before the modal opened, so it can be handed back on close. */
  const restoreTo = useRef<HTMLElement | null>(null)

  useEffect(() => {
    if (!active) return

    restoreTo.current =
      document.activeElement instanceof HTMLElement ? document.activeElement : null

    /**
     * Focus the first control inside, or the panel itself.
     *
     * On the capture sheet this is what makes the design's 5-second budget achievable: *"the field is
     * focused before the user decides anything, and the keyboard is already up."* Focusing on the way
     * up rather than after the animation is the difference between one tap and two. On the photo
     * viewer the first control is Close, which is deliberately what a keyboard or switch-control user
     * arriving at a full-screen image wants under their thumb — focusing the container instead would
     * make Escape their only exit without pressing Tab first.
     */
    const focusable = panelRef.current?.querySelectorAll<HTMLElement>(FOCUSABLE)
    const first = focusable?.[0]
    if (first !== undefined) first.focus()
    else panelRef.current?.focus()

    /**
     * Lock the page behind. Without this, scrolling inside the modal chains to the document once it
     * hits its end — the modal stays put and the screen underneath slides, which looks like a bug.
     * `overscroll-behavior` on the panel handles the same thing for touch.
     */
    const previousOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'

    function onKeyDown(event: KeyboardEvent) {
      if (event.key === 'Escape') {
        event.preventDefault()
        onClose()
        return
      }

      if (event.key !== 'Tab') return

      // Queried on every Tab rather than cached, because the content changes — the sheet's optional
      // fields unfold and its saved step replaces the form entirely. Cheap, and it cannot go stale.
      const nodes = panelRef.current?.querySelectorAll<HTMLElement>(FOCUSABLE)
      if (nodes === undefined || nodes.length === 0) return
      const list = Array.from(nodes)
      const firstNode = list[0]
      const lastNode = list[list.length - 1]
      if (firstNode === undefined || lastNode === undefined) return

      if (event.shiftKey && document.activeElement === firstNode) {
        event.preventDefault()
        lastNode.focus()
      } else if (!event.shiftKey && document.activeElement === lastNode) {
        event.preventDefault()
        firstNode.focus()
      }
    }

    document.addEventListener('keydown', onKeyDown)
    return () => {
      document.removeEventListener('keydown', onKeyDown)
      document.body.style.overflow = previousOverflow
      restoreTo.current?.focus()
    }
  }, [active, onClose, panelRef])
}
